/**
 * Central API Client
 *
 * Single source of axios configuration for the app. Two pre-configured instances
 * are exported, differing ONLY in base URL:
 *   - default export `apiClient` -> root API base   (getApiBaseUrl)
 *   - named export  `v1Backend`  -> /api/v1 API base (getApiV1BaseUrl)
 *
 * Both share identical auth (Bearer token), headers (bxiapikey), credentials,
 * timeout, and error logging. Do NOT create axios instances elsewhere or import
 * axios directly in hooks/components - always use one of these two.
 *
 * Base URL is configured from environment variables (see config/api.js):
 * - REACT_APP_API_URL or REACT_APP_BACKEND_URL
 */

import axios from 'axios';
import { getApiBaseUrl, getApiV1BaseUrl, getBxiApiKey } from 'config/api';
import { getStoredToken } from 'utils/authToken';

/** Build an axios instance with the shared BXI config (auth, headers, error logging). */
function createApiClient(baseURL) {
  const instance = axios.create({
    baseURL,
    withCredentials: true,
    timeout: 30000,
    headers: {
      bxiapikey: getBxiApiKey(),
    },
  });

  instance.interceptors.request.use((config) => {
    const token = getStoredToken();
    if (token) {
      config.headers = config.headers ?? {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  instance.interceptors.response.use(
    (res) => res,
    (err) => {
      const message = err?.response?.data?.message ?? err?.message ?? 'Request failed';
      const status = err?.response?.status;
      if (process.env.NODE_ENV === 'development') {
        console.warn('[API error]', status, message, err?.config?.url);
      }
      return Promise.reject(err);
    }
  );

  return instance;
}

/** Root API base. Default export used by React Query hooks and general API calls. */
const apiClient = createApiClient(getApiBaseUrl());

/**
 * /api/v1 API base. Formerly the separate instance in services/hooks/axios.js
 * (imported everywhere as `v1Backend`). Used by auth/login/onboarding flows.
 */
export const v1Backend = createApiClient(getApiV1BaseUrl());

export default apiClient;
